/** Controllo dell'IBAN (stesse regole di ContactDataNormalizer.ValidIban sul backend) e lettura di ABI/CAB italiani. */
export interface IbanCheck { iban: string; valid: boolean; abi: string; cab: string; bank: string }

/** Lunghezze ISO 13616 dei paesi più comuni; per gli altri valgono i limiti generali 15..34. */
const LENGTHS: Record<string, number> = {
  IT: 27, SM: 27, VA: 22, DE: 22, FR: 27, ES: 24, GB: 22, CH: 21, LI: 21, NL: 18, BE: 16, AT: 20, LU: 20, PT: 25,
  IE: 22, MT: 31, MC: 27, GR: 27, CY: 28, SI: 19, SK: 24, HR: 21, RO: 24, BG: 22, PL: 28, CZ: 24, HU: 28, DK: 18,
  SE: 24, FI: 18, NO: 15,
}

/** Nomi leggibili delle banche più diffuse; per le altre vale il nome dell'elenco ABI/CAB (vedi lookupAbiCab). */
const BANKS: Record<string, string> = {
  '03069': 'Intesa Sanpaolo', '02008': 'UniCredit', '05034': 'Banco BPM', '05387': 'BPER Banca',
  '01030': 'Banca Monte dei Paschi di Siena', '01005': 'BNL - BNP Paribas', '07601': 'Poste Italiane - BancoPosta',
  '03015': 'FinecoBank', '03268': 'Banca Sella', '03032': 'Credem', '06230': 'Crédit Agricole Italia',
  '03104': 'Deutsche Bank', '05696': 'Banca Popolare di Sondrio', '03062': 'Banca Mediolanum', '03475': 'ING Bank',
}

export const normalizeIban = (raw: string | null | undefined) => String(raw ?? '').replace(/[\s\-._]/g, '').toUpperCase()

export function validIban(raw: string | null | undefined): boolean {
  const v = normalizeIban(raw)
  if (v.length < 15 || v.length > 34 || !/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(v)) return false
  // ISO 7064: le cifre di controllo 00, 01 e 99 non sono mai valide.
  if (['00', '01', '99'].includes(v.slice(2, 4))) return false
  const len = LENGTHS[v.slice(0, 2)]
  if (len && v.length !== len) return false
  let rest = 0
  for (const c of v.slice(4) + v.slice(0, 4)) {
    rest = c >= 'A' ? (rest * 100 + c.charCodeAt(0) - 55) % 97 : (rest * 10 + Number(c)) % 97
  }
  return rest === 1
}

export function checkIban(raw: string | null | undefined): IbanCheck {
  const iban = normalizeIban(raw)
  const valid = validIban(iban)
  // IT/SM: IT + 2 controllo + CIN + ABI (5) + CAB (5) + conto (12).
  const it = valid && /^(IT|SM)/.test(iban)
  const abi = it ? iban.slice(5, 10) : ''
  const cab = it ? iban.slice(10, 15) : ''
  return { iban, valid, abi, cab, bank: (abi && BANKS[abi]) || '' }
}

/**
 * Banca e agenzia da ABI/CAB, lette dall'elenco pubblico servito dal CRM stesso (public/abicab/{abi}.json, uno per
 * banca): l'IBAN non esce mai verso terzi. Restituisce stringhe vuote se la banca o lo sportello non sono in elenco.
 */
const abiCache = new Map<string, Promise<{ n: string; c: Record<string, string> } | null>>()

export async function lookupAbiCab(abi: string, cab: string): Promise<{ bank: string; agency: string }> {
  if (!/^\d{5}$/.test(abi)) return { bank: '', agency: '' }
  let p = abiCache.get(abi)
  if (!p) {
    p = fetch(`/abicab/${abi}.json`).then(r => (r.ok ? r.json() : null)).catch(() => null)
    abiCache.set(abi, p)
  }
  const d = await p
  return { bank: BANKS[abi] || d?.n || '', agency: d?.c?.[cab] || '' }
}
